import React, { useEffect, useState, useCallback, useRef } from 'react';
import Footer from "../../primitives/Footer";
import BarChart from "./BarChart";
import DonutChart from "./DonutChart";
import ToggleButton from './ToggleButton';
import ClockGreen from "../../assets/icons/clock-green-icon.svg";
import ClockGray from "../../assets/icons/clock-gray-icon.svg";
import { useSelector, useDispatch } from 'react-redux';
import { fetcher } from '../../../logic/util/hooks';
import Loader from '../../primitives/Loader/Loader';
import toast from 'react-hot-toast';
import {
    setYearlyData,
    setFrequencyData,
    setRefreshing,
    setRefreshError,
    selectYearlyData,
    selectIsRefreshing,
    selectLastRefreshed,
} from '../../../features/stats/statsSlice';

export const timeSavings = (evaluating: any, blocked: any): { hours: number; minutes: number } => {
    const evaluatingNumber = Number(evaluating);
    const blockedNumber = Number(blocked);
    const totalSeconds = (evaluatingNumber + blockedNumber) * 10;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return { hours, minutes };
};

interface FrequencyBasedResponse {
    approved: number;
    evaluating: number;
    blocked: number;
    total: number;
}

interface MonthlySeriesData {
    name: string;
    data: number[];
}

type FrequencyKey = "monthly" | "yearToDate" | "allTime" | "last60days";

// Map toggle values to API frequency parameter
const frequencyApiMap: Record<string, string> = {
    monthly: 'monthly',
    yearToDate: 'yearToDate',
    allTime: 'allTime',
};

const formatLastRefreshed = (timestamp: number | null): string => {
    if (!timestamp) return 'Never';
    
    const now = Date.now();
    const diff = now - timestamp;
    
    // Less than 1 minute
    if (diff < 60000) {
        return 'Just now';
    }
    
    // Less than 1 hour
    if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }
    
    // Less than 24 hours
    if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    
    // Format as date
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const StatsDashboard: React.FC = () => {
    const dispatch = useDispatch();
    const [activeToggle, setActiveToggle] = useState<FrequencyKey>('monthly');
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const initialLoadRef = useRef(false);
    
    const { user } = useSelector((state: any) => state.auth);
    const yearlyDataCache = useSelector(selectYearlyData);
    const isRefreshing = useSelector(selectIsRefreshing);
    const lastRefreshed = useSelector(selectLastRefreshed);
    
    // Get frequency data from Redux
    const frequencyDataCache = useSelector((state: any) => state.stats.frequencyData);
    
    // Current frequency data based on toggle
    const currentFrequencyData = frequencyDataCache[activeToggle]?.data;
    const sixtyDaysData = frequencyDataCache.last60days?.data;
    const yearlyData = yearlyDataCache.data;

    // Poll for job completion
    const pollUntilComplete = useCallback(async (
        url: string,
        checkComplete: (response: any) => boolean,
        onComplete: (data: any) => void,
        maxAttempts: number = 30,
        interval: number = 2000
    ): Promise<boolean> => {
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                const response = await fetcher(url);
                
                // Check if job is complete (we have actual data, not jobId)
                if (checkComplete(response)) {
                    onComplete(response);
                    return true;
                }
                
                // If still in progress, wait before next poll
                if (attempt < maxAttempts - 1) {
                    await new Promise(resolve => setTimeout(resolve, interval));
                }
            } catch (error) {
                console.error('Error polling:', error);
                return false;
            }
        }
        return false; // Timeout
    }, []);

    // Fetch yearly data with proper job polling
    const fetchYearlyData = useCallback(async (reset: boolean = false): Promise<boolean> => {
        try {
            const url = `/resources/messages/${user.id}/messages/month-date${reset ? '?reset=true' : ''}`;
            const response = await fetcher(url);
            
            // Check if it's a job response (jobId present) or cached data
            if (response && typeof response === 'object' && !Array.isArray(response) && 'jobId' in response) {
                // Job was queued, poll until complete
                return await pollUntilComplete(
                    `/resources/messages/${user.id}/messages/month-date`,
                    (res) => {
                        // Check if it's actual data (array) and not a job response
                        const isData = Array.isArray(res);
                        const isJob = res && typeof res === 'object' && !Array.isArray(res) && 'jobId' in res;
                        return isData && !isJob;
                    },
                    (data) => {
                        if (Array.isArray(data)) {
                            dispatch(setYearlyData({ data }));
                        }
                    },
                    30, // max 30 attempts
                    2000 // 2 second intervals
                );
            } else if (Array.isArray(response)) {
                // Direct data response (cached) - can be empty array
                dispatch(setYearlyData({ data: response }));
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error("Error fetching yearly data:", error);
            return false;
        }
    }, [user?.id, dispatch, pollUntilComplete]);

    // Fetch frequency data with proper job polling
    const fetchFrequencyData = useCallback(async (frequency: FrequencyKey, reset: boolean = false): Promise<boolean> => {
        try {
            const apiFrequency = frequencyApiMap[frequency] || frequency;
            const url = `/resources/messages/${user.id}/messages?frequency=${apiFrequency}${reset ? '&reset=true' : ''}`;
            const response = await fetcher(url);
            
            if (response && typeof response === 'object' && !Array.isArray(response) && 'jobId' in response) {
                // Job was queued, poll until complete
                return await pollUntilComplete(
                    `/resources/messages/${user.id}/messages?frequency=${apiFrequency}`,
                    (res) => res && typeof res === 'object' && typeof res.approved !== 'undefined' && !('jobId' in res),
                    (data) => {
                        dispatch(setFrequencyData({ frequency, data }));
                    },
                    30, // max 30 attempts
                    2000 // 2 second intervals
                );
            } else if (response && typeof response === 'object' && typeof response.approved !== 'undefined') {
                // Direct data response (cached)
                dispatch(setFrequencyData({ frequency, data: response }));
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error(`Error fetching ${frequency} data:`, error);
            return false;
        }
    }, [user?.id, dispatch, pollUntilComplete]);

    const handleRefreshData = useCallback(async (silent: boolean = false, forceReset: boolean = true) => {
        if (!user?.id) {
            if (!silent) toast.error('No account selected');
            return;
        }

        dispatch(setRefreshing(true));
        if (!silent) {
            toast.loading('Refreshing stats data...', { id: 'refresh-stats' });
        }

        try {
            // Fetch all data in parallel. Use reset=true only when user clicks refresh button
            const results = await Promise.allSettled([
                fetchYearlyData(forceReset),
                fetchFrequencyData('monthly', forceReset),
                fetchFrequencyData('yearToDate', forceReset),
                fetchFrequencyData('allTime', forceReset),
                fetchFrequencyData('last60days', forceReset),
            ]);

            const allSucceeded = results.every(r => r.status === 'fulfilled' && r.value);
            
            if (allSucceeded) {
                if (!silent) {
                    toast.success('Stats data refreshed successfully', { id: 'refresh-stats' });
                }
                dispatch(setRefreshError(null));
            } else {
                const failedCount = results.filter(r => r.status === 'rejected' || !r.value).length;
                if (!silent) {
                    toast.error(`Some data failed to refresh (${failedCount}/5)`, { id: 'refresh-stats' });
                }
                dispatch(setRefreshError('Partial refresh failure'));
            }
        } catch (error) {
            console.error('Error refreshing data:', error);
            if (!silent) {
                toast.error('Failed to refresh stats data', { id: 'refresh-stats' });
            }
            dispatch(setRefreshError('Refresh failed'));
        } finally {
            dispatch(setRefreshing(false));
        }
    }, [user?.id, dispatch, fetchYearlyData, fetchFrequencyData]);

    // Initial load - fetch data when user lands on stats page
    useEffect(() => {
        if (!initialLoadRef.current && user?.id && handleRefreshData) {
            initialLoadRef.current = true;
            // Call APIs without reset to get cached data or start jobs
            // This will trigger jobs if values are not cached in Redis yet
            handleRefreshData(true, false);
            setInitialLoadDone(true);
        }
    }, [user?.id, handleRefreshData]);

    const handleToggleMonthly = () => {
        setActiveToggle('monthly');
    };

    const handleToggleYearToDate = () => {
        setActiveToggle('yearToDate');
    };

    const handleToggleAllTime = () => {
        setActiveToggle('allTime');
    };

    const getDonutChartData = (): number[] => {
        if (!currentFrequencyData) return [0, 0, 0];
        // Return a new array to avoid ApexCharts mutating frozen Redux state
        return [Number(currentFrequencyData.approved), Number(currentFrequencyData.evaluating), Number(currentFrequencyData.blocked)];
    };

    const calculateTimeSavings = (
        data: FrequencyBasedResponse | null,
        sixtyDays: FrequencyBasedResponse | null
    ) => {
        const frequencyBasedData = data ? timeSavings(data.evaluating, data.blocked) : { hours: 0, minutes: 0 };
        const last60Days = sixtyDays ? timeSavings(sixtyDays.evaluating, sixtyDays.blocked) : { hours: 0, minutes: 0 };
        return { frequencyBasedData, last60Days };
    };
    
    const { frequencyBasedData, last60Days } = calculateTimeSavings(currentFrequencyData, sixtyDaysData);

    const barChartCategories = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Bar Chart series - deep clone to prevent ApexCharts from mutating frozen Redux state
    const barChartData = (): MonthlySeriesData[] => {
        if (!yearlyData || yearlyData.length === 0) {
            return [
                { name: 'Approved', data: Array(12).fill(0) },
                { name: 'Evaluating', data: Array(12).fill(0) },
                { name: 'Blocked', data: Array(12).fill(0) },
            ];
        }

        // Validate data structure
        const validData = yearlyData.filter(series => 
            series && 
            typeof series === 'object' && 
            series.name && 
            Array.isArray(series.data)
        );

        if (validData.length === 0) {
            return [
                { name: 'Approved', data: Array(12).fill(0) },
                { name: 'Evaluating', data: Array(12).fill(0) },
                { name: 'Blocked', data: Array(12).fill(0) },
            ];
        }

        // Deep clone to avoid "object is not extensible" error from ApexCharts
        const chartData = validData.map(series => ({
            name: series.name,
            data: [...series.data]
        }));
        return chartData;
    };
    const barChartSeries = barChartData();

    // Show initial loader only if doing first load and refreshing
    if (!initialLoadDone && isRefreshing) {
        return <div className="flex justify-center items-center h-screen"><Loader /></div>;
    }

    return (
        <div className="h-full w-full flex flex-col min-h-screen">
            <div className="py-6 md:px-28 px-4 w-full">
                <div className="flex flex-col md:flex-row gap-4 justify-center md:justify-between sm:items-center w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                        <p className='text-2xl font-bold'>Stats</p>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => handleRefreshData(false, true)}
                                disabled={isRefreshing || !user?.id}
                                className={`flex items-center gap-2 px-4 py-2 rounded-full border-2 transition-all duration-200 ${
                                    isRefreshing || !user?.id
                                        ? 'border-gray3 text-gray2 cursor-not-allowed bg-gray_light'
                                        : 'border-green_primary text-green_primary hover:bg-green_primary hover:text-white'
                                }`}
                            >
                                <svg 
                                    className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth={2} 
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                                    />
                                </svg>
                                <span className="text-sm font-semibold">
                                    {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
                                </span>
                            </button>
                            {lastRefreshed && (
                                <span className="text-xs text-gray2 hidden sm:inline">
                                    Last refreshed: {formatLastRefreshed(lastRefreshed)}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-col md:flex-row border-2 px-5 py-3 border-green_primary rounded-md justify-between md:space-x-5 space-y-5 md:space-y-0">
                        <div className="flex justify-between space-x-4">
                            <span>Last 30 days</span>
                            <ToggleButton handleToggle={handleToggleMonthly} enabled={activeToggle === 'monthly'} />
                        </div>
                        <div className="flex justify-between space-x-4">
                            <span>Year to date</span>
                            <ToggleButton handleToggle={handleToggleYearToDate} enabled={activeToggle === 'yearToDate'} />
                        </div>
                        <div className="flex justify-between space-x-4">
                            <span>All time</span>
                            <ToggleButton handleToggle={handleToggleAllTime} enabled={activeToggle === 'allTime'} />
                        </div>
                    </div>
                </div>
                
                {/* Mobile last refreshed */}
                {lastRefreshed && (
                    <div className="sm:hidden mt-2 text-xs text-gray2">
                        Last refreshed: {formatLastRefreshed(lastRefreshed)}
                    </div>
                )}

                {/* Loading overlay for refresh */}
                <div className={`relative ${isRefreshing ? 'opacity-60 pointer-events-none' : ''}`}>
                    <div className='max-h-fit flex flex-col lg:flex-row justify-center w-full lg:space-x-10 mt-10'>
                        <div className='w-full p-4 rounded-md shadow-lg border border-gray4'>
                            <p className='text-lg font-bold'>Time savings</p>
                            <div className={`flex md:mx-20 ${activeToggle === "allTime" ? "justify-start" : activeToggle === "yearToDate" ? "justify-start" : "justify-between"} mt-5`}>
                                <div className='md:pb-2'>
                                <p className='text-lg text-gray2 font-bold'>{`${activeToggle === "allTime" ? "All time" : activeToggle === "yearToDate" ? "Year to date" : "Last 30 days"}`}</p>
                                    <div className='flex justify-center items-center mt-4 -ml-2'>
                                        <img src={frequencyBasedData.hours > 0 || frequencyBasedData.minutes ? ClockGreen : ClockGray} className='w-6 h-6 md:w-10 md:h-10 mr-1' alt="clock" />
                                        <p className={`text-lg md:text-2xl font-bold ${frequencyBasedData.hours > 0 || frequencyBasedData.minutes > 0
                                            ? "text-green_primary" : "text-gray1"}`}>{frequencyBasedData.hours > 0 || frequencyBasedData.minutes > 0
                                                ? `${frequencyBasedData.hours}h ${frequencyBasedData.minutes}m`
                                                : "--"}
                                        </p>
                                    </div>
                                </div>
                                <div className={`${activeToggle==='yearToDate' || activeToggle==='allTime' ? "hidden" : ""}`}>
                                    <p className='text-lg text-gray2 font-bold'>Last 60 days</p>
                                    <div className='flex justify-center items-center mt-4 -ml-2'>
                                        <img src={last60Days.hours > 0 || last60Days.minutes ? ClockGreen : ClockGray} className='w-6 h-6 md:w-10 md:h-10 mr-1' alt="clock" />
                                        <p className={`text-lg md:text-2xl font-bold ${last60Days.hours > 0 || last60Days.minutes > 0
                                            ? "text-green_primary" : "text-gray1"}`}>{last60Days.hours > 0 || last60Days.minutes > 0
                                                ? `${last60Days.hours}h ${last60Days.minutes}m`
                                                : "--"}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='w-full h-full lg:max-w-md p-4 rounded-md shadow-lg border border-gray4 mt-5 lg:mt-0'>
                            <p className='text-lg font-bold'>Shielded from inbox</p>
                            <div>
                                <DonutChart series={getDonutChartData()} />
                            </div>
                        </div>
                    </div>
                    <div className='flex flex-col w-full rounded-md shadow-lg border border-gray4 mt-10'>
                        <div className='w-full flex flex-col md:flex-row justify-between items-center p-4'>
                            <p className='text-lg font-bold'>Month over month</p>
                            <div className='flex space-x-5'>
                                <div className='flex space-x-2 items-center'>
                                    <div className='flex w-1.5 h-1.5 rounded-full bg-green_primary_text'></div>
                                    <p className='text-green_primary'>Approved</p>
                                </div>
                                <div className='flex space-x-2 items-center'>
                                    <div className='flex w-1.5 h-1.5 rounded-full bg-yellow100'></div>
                                    <p className='text-yellow100'>Evaluating</p>
                                </div>
                                <div className='flex space-x-2 items-center'>
                                    <div className='flex w-1.5 h-1.5 rounded-full bg-red_error'></div>
                                    <p className='text-red_error'>Blocked</p>
                                </div>
                            </div>
                        </div>
                        <div className='w-full overflow-x-auto'>
                            <BarChart series={barChartSeries} categories={barChartCategories} />
                        </div>
                    </div>
                </div>
                <div className='mt-10 flex flex-col'>
                    <p className='text-gray1 font-semibold text-sm'>Footnotes:</p>
                    <li className='text-gray2 text-sm list-disc'>Email filtering is updated in real time. Using drag & drop to change the status of a sender will affect the reported # of filtered emails.</li>
                    <li className='text-gray2 text-sm list-disc mt-2'>Formula for time savings = Total # of Blocked emails + Total # of Evaluating emails over a given time period X 10 seconds / 3600</li>
                </div>
            </div>
        </div>
    );
}

export default StatsDashboard;
